import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';

import { LEASE } from '@makemydeal/dr-platform-types';
import { TRADE_OWNERSHIP_TYPE } from '@makemydeal/dr-shared-ui-utils';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as lenderListSelectors from '@makemydeal/dr-dash-store/selectors/lenderListSelectors';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import type { PriorLeaseBalance, PriorLeaseBalanceApplyMethod } from '@makemydeal/dr-offer-redux';
import type { TradeInState } from '@makemydeal/dr-dash-store/types/tradeInTypes';

import { isVinValid } from '../../utils/VinValidations';
import { LEASE_APPLY_METHOD_OPTIONS, NON_LEASE_APPLY_METHOD_OPTIONS, PRIOR_BALANCE_MAX_AMOUNT } from './constants';

export type PriorBalanceFormData = {
    isEnabled: boolean;
    amount?: number;
    payableTo?: string;
    vin?: string;
    lenderOwed?: string;
    applyMethod: PriorLeaseBalanceApplyMethod;
};

const resolveDefaultVin = (tradeIns?: TradeInState[]): string | undefined => {
    if (!Array.isArray(tradeIns)) {
        return undefined;
    }

    const leasedTrade = tradeIns.find((tradeIn) => tradeIn.vin && tradeIn.ownershipType === TRADE_OWNERSHIP_TYPE.LEASE);
    const fallbackTrade = tradeIns.find((tradeIn) => tradeIn.vin);
    return leasedTrade?.vin || fallbackTrade?.vin || undefined;
};

const resolveApplyMethod = (
    applyMethod: PriorLeaseBalanceApplyMethod | undefined,
    supported: PriorLeaseBalanceApplyMethod[]
): PriorLeaseBalanceApplyMethod => (applyMethod && supported.includes(applyMethod) ? applyMethod : 'Capitalized');

const isPriorBalanceSaved = (priorLeaseBalance: PriorLeaseBalance): boolean =>
    typeof priorLeaseBalance.amount === 'number' || Boolean(priorLeaseBalance.applyMethod);

const toFormData = (
    priorLeaseBalance: PriorLeaseBalance,
    supportedApplyMethods: PriorLeaseBalanceApplyMethod[],
    defaultVin?: string
): PriorBalanceFormData => ({
    isEnabled: isPriorBalanceSaved(priorLeaseBalance),
    amount: priorLeaseBalance.amount,
    payableTo: priorLeaseBalance.payableTo,
    vin: priorLeaseBalance.vin ?? (isPriorBalanceSaved(priorLeaseBalance) ? undefined : defaultVin),
    lenderOwed: priorLeaseBalance.lenderOwed,
    applyMethod: resolveApplyMethod(priorLeaseBalance.applyMethod, supportedApplyMethods)
});

const toPriorLeaseBalance = (formData: PriorBalanceFormData): PriorLeaseBalance => {
    if (!formData.isEnabled) {
        return {};
    }

    return {
        ...(typeof formData.amount === 'number' ? { amount: formData.amount } : {}),
        ...(formData.payableTo ? { payableTo: formData.payableTo } : {}),
        ...(formData.vin ? { vin: formData.vin } : {}),
        ...(formData.lenderOwed ? { lenderOwed: formData.lenderOwed } : {}),
        applyMethod: formData.applyMethod
    };
};

export type UsePriorBalanceFormResult = ReturnType<typeof usePriorBalanceForm>;

export const usePriorBalanceForm = (isOpen = true) => {
    const dispatch = useDispatch();

    const savedPriorLeaseBalance = useSelector(offerReduxSelectors.getPriorLeaseBalance, isEqual);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const lenderList = useSelector(lenderListSelectors.getLenderList, shallowEqual);
    const tradeIns = useSelector(tradeInsSelectors.getActiveTradeIns, shallowEqual);

    const applyMethodOptions = currentOfferType === LEASE ? LEASE_APPLY_METHOD_OPTIONS : NON_LEASE_APPLY_METHOD_OPTIONS;
    const supportedApplyMethods = useMemo(() => applyMethodOptions.map((option) => option.value), [applyMethodOptions]);

    const defaultVin = useMemo(() => resolveDefaultVin(tradeIns), [tradeIns]);
    const savedFormData = useMemo(
        () => toFormData(savedPriorLeaseBalance, supportedApplyMethods, defaultVin),
        [savedPriorLeaseBalance, supportedApplyMethods, defaultVin]
    );
    const savedFormDataRef = useRef(savedFormData);
    savedFormDataRef.current = savedFormData;

    const [formData, setFormData] = useState<PriorBalanceFormData>(savedFormData);

    useEffect(() => {
        setFormData(savedFormDataRef.current);
    }, [savedPriorLeaseBalance]);

    useEffect(() => {
        if (!isOpen) {
            setFormData(savedFormDataRef.current);
        }
    }, [isOpen]);

    useEffect(() => {
        setFormData((previous) => {
            const applyMethod = resolveApplyMethod(previous.applyMethod, supportedApplyMethods);
            return applyMethod === previous.applyMethod ? previous : { ...previous, applyMethod };
        });
    }, [supportedApplyMethods]);

    const previousDefaultVinRef = useRef(defaultVin);

    useEffect(() => {
        const previousDefaultVin = previousDefaultVinRef.current;
        previousDefaultVinRef.current = defaultVin;

        if (previousDefaultVin === defaultVin) {
            return;
        }

        setFormData((previous) => (previous.vin === previousDefaultVin ? { ...previous, vin: defaultVin } : previous));
    }, [defaultVin]);

    const lenderOptions = useMemo(
        () =>
            (Array.isArray(lenderList) ? lenderList : [])
                .filter((lender) => Boolean(lender.lenderName))
                .map((lender) => ({ value: lender.lenderName, label: lender.lenderName })),
        [lenderList]
    );

    const isAmountInRange =
        typeof formData.amount !== 'number' || (formData.amount >= 0 && formData.amount <= PRIOR_BALANCE_MAX_AMOUNT);
    const isValid = !formData.isEnabled || (isAmountInRange && isVinValid(formData.vin || ''));
    const isDirty = !isEqual(formData, savedFormData);

    const updateField = useCallback(<K extends keyof PriorBalanceFormData>(field: K, value: PriorBalanceFormData[K]) => {
        setFormData((previous) => ({ ...previous, [field]: value }));
    }, []);

    const toggleEnabled = useCallback((isEnabled: boolean) => {
        setFormData((previous) =>
            isEnabled ? { ...previous, isEnabled } : { ...previous, isEnabled, amount: undefined, applyMethod: 'Capitalized' }
        );
    }, []);

    const apply = useCallback(() => {
        if (!isValid || !isDirty) {
            return;
        }

        const priorLeaseBalance = toPriorLeaseBalance(formData);
        const savedAmount = savedFormData.isEnabled ? savedFormData.amount : undefined;
        const draftAmount = formData.isEnabled ? formData.amount : undefined;
        const affectsPayment =
            savedAmount !== draftAmount || (typeof draftAmount === 'number' && savedFormData.applyMethod !== formData.applyMethod);

        setFormData(toFormData(priorLeaseBalance, supportedApplyMethods, defaultVin));
        dispatch(offerReduxActionCreators.setPriorLeaseBalance(priorLeaseBalance, affectsPayment));
    }, [dispatch, formData, savedFormData, isValid, isDirty, supportedApplyMethods, defaultVin]);

    return useMemo(
        () => ({
            formData,
            applyMethodOptions,
            lenderOptions,
            defaultVin,
            isValid,
            isDirty,
            updateField,
            toggleEnabled,
            apply
        }),
        [formData, applyMethodOptions, lenderOptions, defaultVin, isValid, isDirty, updateField, toggleEnabled, apply]
    );
};
